import * as React from "react";
import type { DragPayload } from "./types";

/** Press-and-hold delay before a touch becomes a drag; short enough to feel
 * immediate, long enough that a plain tap or the start of a scroll gesture
 * doesn't accidentally arm one. */
const HOLD_MS = 180;
/** Movement past this before the hold timer fires cancels arming instead: the
 * touch is treated as a scroll, not a drag attempt. */
const CANCEL_PX = 10;

export interface TouchGhost {
  x: number;
  y: number;
  label: string;
}

interface ActiveTouch {
  armed: boolean;
  holdTimer: number;
  startX: number;
  startY: number;
  payload: DragPayload;
  label: string;
  lastTarget: Element | null;
}

/** A plain object standing in for DataTransfer: the real one can't be built
 * off a touch, and every handler on the receiving end only ever sets
 * dropEffect or calls setData/getData. */
function fakeDataTransfer(): DataTransfer {
  return {
    dropEffect: "",
    effectAllowed: "",
    setData: () => {},
    getData: () => "",
  } as unknown as DataTransfer;
}

function dispatchDragLike(type: string, target: Element, x: number, y: number) {
  const event = new Event(type, { bubbles: true, cancelable: true }) as Event & {
    clientX: number;
    clientY: number;
    dataTransfer: DataTransfer;
  };
  event.clientX = x;
  event.clientY = y;
  event.dataTransfer = fakeDataTransfer();
  target.dispatchEvent(event);
}

function findDropTarget(x: number, y: number): Element | null {
  // Not implemented in every jsdom version (though real browsers all have it).
  if (typeof document.elementFromPoint !== "function") return null;
  const el = document.elementFromPoint(x, y);
  return el ? el.closest("[data-drop-target]") : null;
}

/**
 * Bridges touch input onto the scheduler's existing native-HTML5-drag-and-drop
 * wiring, so phones/tablets get the same drag-to-schedule flow a mouse gets,
 * without a second implementation of the placement/validation logic. Every
 * drop target (TimelineGrid's track, WeekGrid's day cell) already has real
 * onDragOver/onDragLeave/onDrop props from native drag; a touch drag is
 * translated into synthetic dragover/dragleave/drop DOM events dispatched at
 * whatever `[data-drop-target]` element is under the finger (via
 * elementFromPoint), so those same props fire exactly as they would for a
 * mouse-driven native drag.
 *
 * A press-and-hold arms the drag; movement before it fires, past a small
 * threshold, cancels arming instead, so a normal scroll of the Content
 * Library grid or the timeline itself isn't hijacked into a drag.
 */
export function useTouchDragBridge(dragRef: React.MutableRefObject<DragPayload | null>) {
  const [ghost, setGhost] = React.useState<TouchGhost | null>(null);

  // handleMove/handleEnd must keep the same function identity across renders
  // (add/removeEventListener need it), so they're created once, lazily, and
  // read all their state back off this ref rather than a closure.
  const ctl = React.useRef<{
    active: ActiveTouch | null;
    handleMove: (e: TouchEvent) => void;
    handleEnd: (e: TouchEvent) => void;
  } | null>(null);

  if (!ctl.current) {
    const stop = () => {
      const c = ctl.current!;
      if (c.active) window.clearTimeout(c.active.holdTimer);
      document.removeEventListener("touchmove", c.handleMove);
      document.removeEventListener("touchend", c.handleEnd);
      document.removeEventListener("touchcancel", c.handleEnd);
      c.active = null;
      dragRef.current = null;
      setGhost(null);
    };

    const handleMove = (e: TouchEvent) => {
      const active = ctl.current!.active;
      const touch = e.touches[0];
      if (!active || !touch) return;
      if (!active.armed) {
        const dx = Math.abs(touch.clientX - active.startX);
        const dy = Math.abs(touch.clientY - active.startY);
        if (dx > CANCEL_PX || dy > CANCEL_PX) stop();
        return;
      }
      // Real gesture, now committed to a drag: stop the page from also
      // scrolling underneath it.
      e.preventDefault();
      setGhost({ x: touch.clientX, y: touch.clientY, label: active.label });
      const target = findDropTarget(touch.clientX, touch.clientY);
      if (target !== active.lastTarget) {
        if (active.lastTarget) {
          dispatchDragLike("dragleave", active.lastTarget, touch.clientX, touch.clientY);
        }
        active.lastTarget = target;
      }
      if (target) dispatchDragLike("dragover", target, touch.clientX, touch.clientY);
    };

    const handleEnd = (e: TouchEvent) => {
      const active = ctl.current!.active;
      if (!active) return;
      if (active.armed && active.lastTarget) {
        const touch = e.changedTouches[0];
        if (touch) dispatchDragLike("drop", active.lastTarget, touch.clientX, touch.clientY);
      }
      stop();
    };

    ctl.current = { active: null, handleMove, handleEnd };
  }

  /**
   * Wire onto a draggable source's onTouchStart, alongside its existing
   * onDragStart: buildPayload mirrors that handler's dragRef assignment (so,
   * e.g., a program block's grab offset is computed the same way from the
   * touch's position instead of a mouse's), and label is what the floating
   * ghost shows while the drag is armed.
   */
  const bind = React.useCallback(
    (buildPayload: (touch: React.Touch, rect: DOMRect) => DragPayload, label: string) => ({
      onTouchStart: (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const c = ctl.current!;
        const holdTimer = window.setTimeout(() => {
          if (!c.active) return;
          c.active.armed = true;
          dragRef.current = c.active.payload;
          setGhost({ x: touch.clientX, y: touch.clientY, label: c.active.label });
          const target = findDropTarget(touch.clientX, touch.clientY);
          c.active.lastTarget = target;
          if (target) dispatchDragLike("dragover", target, touch.clientX, touch.clientY);
        }, HOLD_MS);
        c.active = {
          armed: false,
          holdTimer,
          startX: touch.clientX,
          startY: touch.clientY,
          payload: buildPayload(touch, rect),
          label,
          lastTarget: null,
        };
        document.addEventListener("touchmove", c.handleMove, { passive: false });
        document.addEventListener("touchend", c.handleEnd);
        document.addEventListener("touchcancel", c.handleEnd);
      },
    }),
    [dragRef],
  );

  React.useEffect(() => {
    return () => {
      const c = ctl.current;
      if (!c) return;
      if (c.active) window.clearTimeout(c.active.holdTimer);
      document.removeEventListener("touchmove", c.handleMove);
      document.removeEventListener("touchend", c.handleEnd);
      document.removeEventListener("touchcancel", c.handleEnd);
    };
  }, []);

  return { bind, ghost };
}

export type TouchDragHandle = ReturnType<typeof useTouchDragBridge>;
